import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Phone, Building2, Calendar, Car, Ticket, MapPin, CheckCircle2, Clock, Circle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CustomerDetailDrawer({ userId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/customers/${userId}/detail`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load user details"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  if (!open) return null;

  const c = data?.customer || {};
  const counters = data?.counters || {};
  const bookings = data?.bookings || [];
  const vehicles = data?.vehicles || [];
  const vouchers = data?.vouchers || [];
  const commercial = data?.commercial_profile;
  const initials = (c.name || c.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const daysUntil = (iso) => {
    if (!iso) return null;
    return Math.ceil((new Date(iso) - new Date()) / 86400000);
  };
  const certChip = (d) => {
    if (d === null) return { label: "No PUC", cls: "bg-muted text-muted-foreground border-border" };
    if (d <= 0) return { label: `Expired ${Math.abs(d)}d`, cls: "bg-red-100 text-red-800 border-red-300" };
    if (d <= 7) return { label: `${d}d left`, cls: "bg-orange-100 text-orange-800 border-orange-300" };
    if (d <= 30) return { label: `${d} days`, cls: "bg-amber-100 text-amber-800 border-amber-300" };
    return { label: `Valid`, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  };
  const statusIcon = (s) => {
    if (s === "completed") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (s === "cancelled") return <Circle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="customer-detail-drawer" side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
        {/* Header */}
        <div className="p-6 border-b border-border bg-gradient-to-br from-primary/5 via-transparent to-primary/5">
          <SheetHeader className="text-left">
            <SheetTitle className="sr-only">User details</SheetTitle>
          </SheetHeader>
          {loading || !data ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-primary font-bold text-lg font-mono">{initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl font-bold tracking-tight truncate" data-testid="cust-detail-name">{c.name || "—"}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <Mail className="w-3 h-3" /> <span className="truncate">{c.email}</span>
                    {c.phone && (<><span>·</span><Phone className="w-3 h-3" /> {c.phone}</>)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge className={`rounded-lg ${c.account_type === "commercial" ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-foreground border-border"}`}>
                      {c.account_type || "individual"}
                    </Badge>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{c.auth_provider || "jwt"}</span>
                    <span className="text-[10px] text-muted-foreground">Joined {(c.created_at || "").slice(0, 10)}</span>
                  </div>
                </div>
              </div>

              {/* Counters */}
              <div className="grid grid-cols-4 gap-2 mt-6">
                {[
                  { label: "Bookings", value: counters.total_bookings || 0 },
                  { label: "Done", value: counters.completed_bookings || 0 },
                  { label: "Vehicles", value: counters.saved_vehicles || 0 },
                  { label: "Vouchers", value: counters.vouchers_used || 0 },
                ].map((k) => (
                  <div key={k.label} className="bg-card border border-border rounded-xl p-3 text-center">
                    <div className="text-xl font-display font-bold text-primary" data-testid={`cust-count-${k.label.toLowerCase()}`}>{k.value}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{k.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Body */}
        {data && (
          <div className="p-6">
            {commercial && (
              <div className="mb-4 border border-primary/20 bg-primary/5 rounded-xl p-3 text-xs" data-testid="cust-commercial">
                <div className="flex items-center gap-1.5 uppercase tracking-[0.2em] text-primary font-semibold text-[10px] mb-1"><Building2 className="w-3 h-3" /> Commercial profile</div>
                <div><b>{commercial.company_name}</b> · {commercial.business_type} · Fleet size {commercial.fleet_size}</div>
                {commercial.gst_number && <div className="text-muted-foreground mt-0.5">GST {commercial.gst_number}</div>}
              </div>
            )}

            <Tabs defaultValue="bookings">
              <TabsList className="rounded-lg bg-secondary border border-border w-full">
                <TabsTrigger data-testid="cust-tab-bookings" value="bookings" className="rounded-lg flex-1"><Calendar className="w-3 h-3 mr-1" /> Bookings ({bookings.length})</TabsTrigger>
                <TabsTrigger data-testid="cust-tab-vehicles" value="vehicles" className="rounded-lg flex-1"><Car className="w-3 h-3 mr-1" /> Vehicles ({vehicles.length})</TabsTrigger>
                <TabsTrigger data-testid="cust-tab-vouchers" value="vouchers" className="rounded-lg flex-1"><Ticket className="w-3 h-3 mr-1" /> Vouchers ({vouchers.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="bookings" className="mt-4 space-y-2" data-testid="cust-bookings-list">
                {bookings.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">No bookings yet.</div>
                ) : bookings.map((b) => (
                  <div key={b.booking_id} className="border border-border rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {statusIcon(b.status)}
                        <div className="min-w-0">
                          <div className="font-medium truncate">{b.partner_snapshot?.store_name || "—"}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.partner_snapshot?.city || "—"} · {b.slot_date} {b.slot_time}</div>
                        </div>
                      </div>
                      <Badge className="rounded-lg bg-secondary text-foreground border-border text-[10px]">{b.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                      <span className="font-mono">{b.vehicle_number}</span>
                      <span className="text-muted-foreground">{b.vehicle_type?.replace("_", " ")} · {b.fuel_type}</span>
                      {b.cert_valid_until && <span className="text-emerald-700">PUC till {b.cert_valid_until}</span>}
                      {b.coupon?.code && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2">
                          <Ticket className="w-3 h-3" /> {b.coupon.code}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="vehicles" className="mt-4 space-y-2" data-testid="cust-vehicles-list">
                {vehicles.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">No saved vehicles.</div>
                ) : vehicles.map((v) => {
                  const chip = certChip(daysUntil(v.cert_valid_until));
                  return (
                    <div key={v.vehicle_id} className="border border-border rounded-xl p-3 flex items-center gap-3">
                      <Car className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-medium">{v.vehicle_number}</div>
                        <div className="text-[11px] text-muted-foreground">{v.vehicle_type?.replace("_", " ")} · {v.fuel_type?.toUpperCase()}{v.nickname ? ` · ${v.nickname}` : ""}</div>
                        {v.cert_valid_until && <div className="text-[11px] text-muted-foreground">Valid till {v.cert_valid_until}</div>}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-full border px-2 py-0.5 ${chip.cls}`}>{chip.label}</span>
                    </div>
                  );
                })}
              </TabsContent>

              <TabsContent value="vouchers" className="mt-4 space-y-2" data-testid="cust-vouchers-list">
                {vouchers.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">No vouchers redeemed yet.</div>
                ) : vouchers.map((v) => {
                  const label = v.discount_type === "free" ? "Complimentary PUC" :
                    v.discount_type === "percent" ? `${v.discount_value}% off` :
                    `₹${v.discount_value} off`;
                  return (
                    <div key={v.coupon_id} className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-bold text-emerald-800">{v.code}</div>
                        <div className="text-[11px] text-emerald-700">{label} · {v.service_scope === "any" ? "any vehicle" : v.service_scope?.replace("_", " ")}</div>
                        {v.redeemed_at && <div className="text-[10px] text-muted-foreground">Redeemed {v.redeemed_at.slice(0, 10)}</div>}
                      </div>
                      <Badge className="rounded-lg bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">{v.status}</Badge>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
